// src/pages/EditBlog.jsx
import React, { useContext, useEffect, useState } from "react";
import { db, storage } from "./firebase/firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useParams, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { UserContext } from "./context/UserContext";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// ✅ Reuse styles from AddBlog
const Wrapper = styled.div`
  padding: 40px;
  max-width: 900px;
  margin: auto;
  color: #fff;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const EditorWrapper = styled.div`
  margin-bottom: 20px;
  background: #fff;
  border-radius: 8px;
  color: #000;
  padding: 10px;
`;

const Button = styled.button`
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #121212;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: linear-gradient(135deg, #00bcd4, #0097a7);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Loader = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid #121212;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: ${spin} 1s linear infinite;
  margin-left: 8px;
`;

const technologyCategories = [
  "React",
  "React Native",
  "Angular",
  "Vue.js",
  "Next.js",
  "Node.js",
  "Express.js",
  "Django",
  "Flask",
  "Spring Boot",
  "Laravel",
  "Ruby on Rails",
  "Python",
  "Java",
  "JavaScript",
  "TypeScript",
  "C#",
  "C++",
  "Go",
  "PHP",
  "Kotlin",
  "Swift",
  "SQL",
  "MongoDB",
  "PostgreSQL",
  "MySQL",
  "Firebase",
  "Supabase",
  "AWS",
  "Google Cloud",
  "Azure",
  "DevOps",
  "Docker",
  "Kubernetes",
  "AI / Machine Learning",
  "Data Science",
  "Cybersecurity",
  "Blockchain",
  "Other",
];

const EditBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("React");
  const [content, setContent] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [existingThumbnail, setExistingThumbnail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      const ref = doc(db, "blogs", id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setTitle(data.title);
        setCategory(data.category);
        setContent(data.content);
        setExistingThumbnail(data.thumbnail);
      }
    };
    fetchBlog();
  }, [id]);

  const handleUpdate = async () => {
    if (!title || !content) {
      alert("⚠️ Title and content are required");
      return;
    }

    setLoading(true);

    try {
      let thumbnailURL = existingThumbnail;

      // if new thumbnail selected
      if (thumbnail) {
        const storageRef = ref(
          storage,
          `blogs/${Date.now()}-${thumbnail.name}`
        );
        await uploadBytes(storageRef, thumbnail);
        thumbnailURL = await getDownloadURL(storageRef);
      }

      const refDoc = doc(db, "blogs", id);
      await updateDoc(refDoc, {
        title,
        category,
        content,
        thumbnail: thumbnailURL,
        updatedAt: new Date(),
        updatedBy: user?.name || "Admin",
      });

      alert("✅ Blog updated successfully!");
      navigate("/admin");
    } catch (err) {
      console.error(err);
      alert("❌ Error updating blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <h2>Edit Blog</h2>
      <Input
        type="text"
        placeholder="Blog Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <Select value={category} onChange={(e) => setCategory(e.target.value)}>
        {technologyCategories.map((tech) => (
          <option key={tech} value={tech.toLowerCase()}>
            {tech}
          </option>
        ))}
      </Select>
      {existingThumbnail && (
        <img
          src={existingThumbnail}
          alt="current thumbnail"
          style={{ width: "200px", marginBottom: "10px", borderRadius: "8px" }}
        />
      )}
      <Input
        type="file"
        accept="image/*"
        onChange={(e) => setThumbnail(e.target.files[0])}
      />
      <EditorWrapper>
        <CKEditor
          editor={ClassicEditor}
          data={content}
          onChange={(event, editor) => setContent(editor.getData())}
        />
      </EditorWrapper>
      <Button onClick={handleUpdate} disabled={loading}>
        {loading ? (
          <>
            Updating...
            <Loader />
          </>
        ) : (
          "Update Blog"
        )}
      </Button>
    </Wrapper>
  );
};

export default EditBlog;
